import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { EmptyState, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Plus, FileStack, Pencil, Copy, Trash2, GripVertical, Loader2, CheckCircle2, ChevronDown, ChevronUp,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);
const emptyItem = () => ({ id: uid(), label: "", description: "", required: true, input_type: "check", material_ids: [] });
const emptySection = () => ({ id: uid(), title: "New Section", items: [emptyItem()] });

export default function Templates() {
  const qc = useQueryClient();
  const [editor, setEditor] = useState(null); // {template, isNew}

  const { data: templates = [], isLoading } = useQuery({ queryKey: ["templates"], queryFn: async () => (await api.get("/templates")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  const activateMutation = useMutation({
    mutationFn: async (id) => api.patch(`/templates/${id}/activate`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["templates"] }); toast.success("Template activated"); },
  });
  const duplicateMutation = useMutation({
    mutationFn: async (t) => api.post(`/templates/${t.id}/duplicate`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["templates"] }); toast.success("Template duplicated"); },
  });

  const openNew = () => setEditor({
    isNew: true,
    template: { name: "", position: settings?.positions?.[0] || "", training_type: settings?.training_types?.[0] || "", sections: [emptySection()] },
  });

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Checklist Templates</h1>
          <p className="text-muted-foreground mt-1">Define the checklist for each position + training type. Editing bumps the version.</p>
        </div>
        <Button className="gap-2 h-11 rounded-xl" onClick={openNew} data-testid="new-template-button"><Plus size={18} /> New Template</Button>
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
        : templates.length === 0 ? <Card><EmptyState icon={FileStack} title="No templates yet" desc="Create your first checklist template." /></Card>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {templates.map((t) => (
              <Card key={t.id} className="p-5 space-y-3" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-heading font-semibold">{t.name}</h3>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.position} · {t.training_type}</div>
                  </div>
                  {t.is_active ? <Badge className="bg-success/15 text-success border-success/30" variant="outline">Active</Badge>
                    : <Badge variant="outline">Inactive</Badge>}
                </div>
                <div className="text-xs text-muted-foreground font-mono">v{t.version} · {t.sections?.length} sections · {t.sections?.reduce((a, s) => a + s.items.length, 0)} items</div>
                <div className="text-[11px] text-muted-foreground">Updated {fmtDate(t.updated_at)}</div>
                <div className="flex gap-2 pt-1 flex-wrap">
                  <Button size="sm" variant="secondary" className="gap-1" onClick={() => setEditor({ isNew: false, template: JSON.parse(JSON.stringify(t)) })} data-testid={`edit-template-${t.id}`}><Pencil size={14} /> Edit</Button>
                  <Button size="sm" variant="ghost" className="gap-1" onClick={() => duplicateMutation.mutate(t)} data-testid={`duplicate-template-${t.id}`}><Copy size={14} /> Duplicate</Button>
                  {!t.is_active && <Button size="sm" variant="ghost" className="gap-1" onClick={() => activateMutation.mutate(t.id)} data-testid={`activate-template-${t.id}`}><CheckCircle2 size={14} /> Activate</Button>}
                </div>
              </Card>
            ))}
          </div>
        )}

      {editor && <TemplateEditor editor={editor} settings={settings} onClose={() => setEditor(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["templates"] }); setEditor(null); }} />}
    </div>
  );
}

function TemplateEditor({ editor, settings, onClose, onSaved }) {
  const [tpl, setTpl] = useState(editor.template);
  const [busy, setBusy] = useState(false);

  const setSection = (si, patch) => setTpl((t) => ({ ...t, sections: t.sections.map((s, i) => i === si ? { ...s, ...patch } : s) }));
  const setItem = (si, ii, patch) => setSection(si, { items: tpl.sections[si].items.map((it, i) => i === ii ? { ...it, ...patch } : it) });
  const addSection = () => setTpl((t) => ({ ...t, sections: [...t.sections, emptySection()] }));
  const removeSection = (si) => setTpl((t) => ({ ...t, sections: t.sections.filter((_, i) => i !== si) }));
  const addItem = (si) => setSection(si, { items: [...tpl.sections[si].items, emptyItem()] });
  const removeItem = (si, ii) => setSection(si, { items: tpl.sections[si].items.filter((_, i) => i !== ii) });
  const moveSection = (si, dir) => {
    const ni = si + dir; if (ni < 0 || ni >= tpl.sections.length) return;
    const s = [...tpl.sections]; [s[si], s[ni]] = [s[ni], s[si]]; setTpl((t) => ({ ...t, sections: s }));
  };

  const save = async () => {
    if (!tpl.name || !tpl.position || !tpl.training_type) { toast.error("Name, position and type are required"); return; }
    setBusy(true);
    try {
      if (editor.isNew) await api.post("/templates", tpl);
      else await api.put(`/templates/${tpl.id}`, tpl);
      toast.success(editor.isNew ? "Template created" : "Template saved as new version");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editor.isNew ? "New Template" : "Edit Template"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2 sm:col-span-3"><Label>Template Name</Label><Input value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} data-testid="template-name-input" /></div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Select value={tpl.position} onValueChange={(v) => setTpl({ ...tpl, position: v })}>
                <SelectTrigger data-testid="template-position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Positions">All Positions</SelectItem>
                  {settings?.positions?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Training Type</Label>
              <Select value={tpl.training_type} onValueChange={(v) => setTpl({ ...tpl, training_type: v })}>
                <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{settings?.training_types?.map((tt) => <SelectItem key={tt} value={tt}>{tt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            {tpl.sections.map((section, si) => (
              <Card key={section.id} className="p-4 space-y-3 bg-muted/30" data-testid={`editor-section-${si}`}>
                <div className="flex items-center gap-2">
                  <GripVertical size={16} className="text-muted-foreground" />
                  <Input value={section.title} onChange={(e) => setSection(si, { title: e.target.value })} className="font-medium" data-testid={`section-title-${si}`} />
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveSection(si, -1)}><ChevronUp size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveSection(si, 1)}><ChevronDown size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeSection(si)}><Trash2 size={14} /></Button>
                </div>
                <div className="space-y-2 pl-6">
                  {section.items.map((item, ii) => (
                    <div key={item.id} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center" data-testid={`editor-item-${si}-${ii}`}>
                      <Input value={item.label} placeholder="Item label" onChange={(e) => setItem(si, ii, { label: e.target.value })} className="flex-1" data-testid={`item-label-${si}-${ii}`} />
                      <Select value={item.input_type} onValueChange={(v) => setItem(si, ii, { input_type: v })}>
                        <SelectTrigger className="w-[160px]" data-testid={`item-type-${si}-${ii}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="check">Checkbox</SelectItem>
                          <SelectItem value="check_with_note">Check + Note</SelectItem>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="textarea">Long Text</SelectItem>
                          <SelectItem value="radio">Choice (radio)</SelectItem>
                          <SelectItem value="signature">Signature</SelectItem>
                          <SelectItem value="info">Info / Note</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                        <Checkbox checked={item.required} onCheckedChange={(v) => setItem(si, ii, { required: !!v })} data-testid={`item-required-${si}-${ii}`} /> Required
                      </label>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeItem(si, ii)}><Trash2 size={14} /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" className="gap-1" onClick={() => addItem(si)} data-testid={`add-item-${si}`}><Plus size={14} /> Add Item</Button>
                </div>
              </Card>
            ))}
            <Button variant="outline" className="gap-2 w-full" onClick={addSection} data-testid="add-section-button"><Plus size={16} /> Add Section</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={save} className="gap-2" data-testid="save-template-button">
            {busy && <Loader2 size={16} className="animate-spin" />} {editor.isNew ? "Create Template" : "Save New Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
